import { useMemo } from 'react'
import { useIntl } from 'react-intl'
import LineBuildingIcon from 'LuxKit/Icons/line/LineBuildingIcon'
import LineClockDollarIcon from 'LuxKit/Icons/line/LineClockDollarIcon'
import LineRefundIcon from 'LuxKit/Icons/line/LineRefundIcon'
import LineSunsetIcon from 'LuxKit/Icons/line/LineSunsetIcon'
import { TOUR_FLEXIBLE_DURATION_RANGE } from 'constants/search'
import { TOUR_V2_OFFER_DUE_DATE_DAY_THRESHOLD } from 'constants/tours'
import type { DurationOption } from 'components/Search/SearchForm/SearchDateInput/FlexibleDatePicker/DurationPicker'

function useTourLabels() {
  const intl = useIntl()

  return useMemo(() => {
    const tourV2BookWithConfidenceItems: Array<App.Tours.V2OfferBookWithConfidenceItem> = [
      {
        title: intl.formatMessage({
          id: 'SKV14WsziqQAnVcfmAj8',
          defaultMessage: 'Book everything in one place',
          description: 'Book with confidence item title for all-in-one booking',
        }),
        description: intl.formatMessage({
          id: '4no5VSRr2npx1rH8pGiL',
          defaultMessage: 'From hotels to flights, experiences and more \u2013 we’ve got your trip covered from start to finish.',
          description: 'Book with confidence item description for all-in-one booking',
        }),
        Icon: LineSunsetIcon,
      },
      {
        title: intl.formatMessage({
          id: 'ChD4Ihr2bHyc1PND2yj8',
          defaultMessage: 'Secure dates with a deposit',
          description: 'Book with confidence item title for deposit booking',
        }),
        description: intl.formatMessage(
          {
            id: 'KynF-LSTV77793bGIGnA',
            defaultMessage: 'Reserve your space now with a deposit, pay the rest {dayThreshold, plural, one {# day} other {# days}} before departure',
            description: 'Book with confidence item description for deposit booking with day threshold',
          },
          { dayThreshold: TOUR_V2_OFFER_DUE_DATE_DAY_THRESHOLD },
        ),
        Icon: LineClockDollarIcon,
      },
      {
        title: intl.formatMessage({
          id: 'CrJ1Hin-6K3dek0u1gvA',
          defaultMessage: '7-day change of mind refund',
          description: 'Book with confidence item title for refund policy in the tours section',
        }),
        description: intl.formatMessage({
          id: 'mzT50M4qG1mil7Rk3dMJ',
          defaultMessage: 'Get a full refund on your booking (excludes cruises, flights, travel protection and the service fee paid on deposit bookings)',
          description: 'Book with confidence item description for refund policy exclusions',
        }),
        Icon: LineRefundIcon,
      },
      {
        title: intl.formatMessage({
          id: 'nhmMuEOy3GTqvNFuecdM',
          defaultMessage: 'Handpicked partners',
          description: 'Book with confidence item title for curated travel partners',
        }),
        description: intl.formatMessage({
          id: 'rpZBe4QQmqIuo__BLEXk',
          defaultMessage: 'We work with the world’s most loved brands in travel',
          description: 'Book with confidence item description for curated travel partners',
        }),
        Icon: LineBuildingIcon,
      },
    ]

    const tourDurationOptions: Array<DurationOption> = [
      {
        title: intl.formatMessage({
          id: '9-1pJ7tG7vmztD9cBLnz',
          defaultMessage: 'Any duration',
          description: 'Tour duration option title for any duration on the tour page',
        }),
        caption: intl.formatMessage({
          id: 'ZbumOrSBS7sMDSw4hYn4',
          defaultMessage: 'I’ll decide later',
          description: 'Tour duration option caption for deciding later in the tours section',
        }),
        id: TOUR_FLEXIBLE_DURATION_RANGE.ANY_DURATION,
      },
      {
        title: intl.formatMessage({
          id: 'EN6Zn03N_g1GQxj56y2P',
          defaultMessage: 'Short tour',
          description: 'Tour duration option title for short tours of 1 to 7 days',
        }),
        caption: intl.formatMessage({
          id: 'kZ7YYlNlcr7_IXgHIFrp',
          defaultMessage: '1 to 7 days',
          description: 'Tour duration option caption for short tour day range',
        }),
        id: TOUR_FLEXIBLE_DURATION_RANGE.SHORT_STAY,
      },
      {
        title: intl.formatMessage({
          id: 'wT4Xju4HgnvJYm2p4MnG',
          defaultMessage: 'Medium tour',
          description: 'Tour duration option title for medium tours of 8 to 14 days',
        }),
        caption: intl.formatMessage({
          id: 'mzisjga0CO2x96M5tafW',
          defaultMessage: '8 to 14 days',
          description: 'Tour duration option caption for medium tour day range',
        }),
        id: TOUR_FLEXIBLE_DURATION_RANGE.MEDIUM_STAY,
      },
      {
        title: intl.formatMessage({
          id: 'PAb4HOU_wfDle4MlbCDi',
          defaultMessage: 'Long tour',
          description: 'Tour duration option title for long tours of 15 or more days',
        }),
        caption: intl.formatMessage({
          id: 'fVqTz-grnwvSCY26FNez',
          defaultMessage: '15+ days',
          description: 'Tour duration option caption for long tour day range',
        }),
        id: TOUR_FLEXIBLE_DURATION_RANGE.LONG_STAY,
      },
    ]

    const locationSearchTitleLabel = intl.formatMessage({
      id: 'E9f7fBwPQkpH3-YedpYG',
      defaultMessage: 'Where',
      description: 'Title label for the tour location search input field',
    })

    const locationSearchPlaceholderLabel = intl.formatMessage({
      id: 't9dUaIJ8pKEtSENLo4sR',
      defaultMessage: 'Search destination or place',
      description: 'Placeholder text for the tour location search input field',
    })

    return {
      tourV2BookWithConfidenceItems,
      tourDurationOptions,
      locationSearchTitleLabel,
      locationSearchPlaceholderLabel,
    }
  }, [intl])
}

export default useTourLabels
